import { useEffect, useState } from "react";
import { useVariant } from "../../../../hooks/useVariant/useVariant";
import { DeliveryAndGuaranteesMessage } from "../../../DeliveryAndGuaranteesMessage/DeliveryAndGuaranteesMessage";
import { UserAcknowledgement } from "../../../UserAcknowledgement/UserAcknowledgement";
import { ProductAccordion } from "../../../ProductAccordion/ProductAccordion";
import { addM2MProductToBasket } from "../../util/addM2MProductToBasket";
import {
  PrimaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { Message as MessageV2 } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import { ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE } from "../../../../constants/constants";
import styles from "./MadeToMeasureReviewModal.module.scss";
import { FeatureFlags, Product, RuntimeConfig } from "../../../../types/types";
import {
  BrowsePageHierarchy,
  ConfigurationRequirementsType,
} from "@/types/prodcatBaseTypes";
import {
  CurtainConfiguration,
  MadeToMeasureConfiguration,
} from "../../configurationTypes";
import RollerBlindsSelection from "./RollerBlindsSelection";
import RomanBlindsSelection from "./RomanBlindsSelection";
import CurtainsSelection from "./CurtainsSelection";
import { formatM2MTitle } from "@/utils/product/formatM2MTitle";
import { dispatchAnalyticsEvent } from "@/utils";
import { getDefaultCategory } from "@/utils/product/getDefaultCategory";
import { ReviewModalBasketSummary } from "./ReviewModalBasketSummary/ReviewModalBasketSummary";

type Props = {
  configuration: MadeToMeasureConfiguration;
  totalPrice: string;
  product: Product;
  runtimeConfig: RuntimeConfig;
  flags: FeatureFlags;
  isOpen: boolean;
  onClose: (e: React.SyntheticEvent) => void;
  configurationReqType: ConfigurationRequirementsType;
};

type basketItem = {
  skuId: string;
  quantity: number;
};

export const MadeToMeasureReviewModal = ({
  configuration,
  totalPrice,
  product,
  runtimeConfig,
  flags,
  isOpen,
  onClose,
  configurationReqType,
}: Props) => {
  const { variant } = useVariant(product);

  const [isA2bSuccess, setIsA2bSuccess] = useState(false);
  const [isA2bError, setIsA2bError] = useState(false);
  const [userAcknowledgementChecked, setUserAcknowledgementChecked] =
    useState(false);
  const [isA2bSubmitting, setIsA2bSubmitting] = useState(false);
  const [quantityAdded, setQuantityAdded] = useState(0);

  async function onClickAddToBasket() {
    setIsA2bSubmitting(true);

    const { success, commerceItems } = await addM2MProductToBasket(
      runtimeConfig.basketApiBaseUrl,
      1,
      variant.aliases.skuId,
      variant.id,
      configurationReqType,
      configuration,
    );

    setIsA2bSubmitting(false);
    setIsA2bSuccess(success);
    setIsA2bError(!success);

    dispatchAnalyticsEvent(
      `addtobasket${success ? "success" : "error"}`,
      {
        type: getProductType(),
        buttonType: "static",
        qtyRequested: 1,
        product: {
          itemPrice: totalPrice.replace("£", ""),
          productId: product.aliases.pimProductId,
          qty: 1,
          services: [],
          sku: variant.aliases.skuId,
        },
      },
      {},
    );

    if (success) {
      if (commerceItems) {
        const itemInBasket = commerceItems.find(
          (commerceitem: any) =>
            (commerceitem as basketItem).skuId === variant.aliases.skuId,
        );
        if (itemInBasket)
          setQuantityAdded((itemInBasket as basketItem).quantity);
      } else {
        setQuantityAdded(1);
      }

      window.dispatchEvent(
        new CustomEvent("jl-events.browser.basketQuantityUpdated"),
      );
    }
  }

  const AddToBasketError = () => {
    return (
      <div data-testid="m2m-a2b-error" className={styles.A2bContainer__error}>
        <MessageV2
          type="error"
          body="Sorry, something went wrong. Please try again later"
        />
      </div>
    );
  };

  useEffect(() => {
    const handleUserAcknowledgement = (event: Event) => {
      const { userAcknowledgementChecked } = (event as CustomEvent).detail;
      setUserAcknowledgementChecked(userAcknowledgementChecked);
    };

    window.addEventListener(
      ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE,
      handleUserAcknowledgement,
    );

    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE,
        handleUserAcknowledgement,
      );
    };
  }, []);

  const getSelection = () => {
    let SelectionComponent = null;
    if (configurationReqType === "MadeToMeasureRollerBlind") {
      SelectionComponent = RollerBlindsSelection;
    }
    if (configurationReqType === "MadeToMeasureRomanBlind") {
      SelectionComponent = RomanBlindsSelection;
    }
    if (configurationReqType === "MadeToMeasureCurtain") {
      SelectionComponent = CurtainsSelection;
    }

    return SelectionComponent ? (
      <SelectionComponent
        configuration={configuration}
        onClose={onClose}
      ></SelectionComponent>
    ) : null;
  };

  const getTitle = () => {
    switch (configurationReqType) {
      case "MadeToMeasureCurtain":
        return "Curtain";
      case "MadeToMeasureRollerBlind":
        return "Roller Blind";
      case "MadeToMeasureRomanBlind":
        return "Roman Blind";
      default:
        return "";
    }
  };

  const getProductType = () => {
    if (configurationReqType === "MadeToMeasureRollerBlind") {
      return "m2mroller";
    }

    if (configurationReqType === "MadeToMeasureRomanBlind") {
      return "m2mroman";
    }

    if (configurationReqType === "MadeToMeasureCurtain") {
      return "m2mcurtain";
    }

    return "";
  };

  const getHeadingType = () => {
    const config = configuration as CurtainConfiguration;

    if (config.headingType.includes("Pencil")) return "PP";
    if (config.headingType.includes("Double")) return "DP";
    if (config.headingType.includes("Triple")) return "TP";
    if (config.headingType.includes("Eyelet")) return "EL";

    return "";
  };

  const getImgUrl = () => {
    switch (configurationReqType) {
      case "MadeToMeasureCurtain":
        return variant.images.alternatives.find((img) =>
          img.endsWith(`WS${getHeadingType()}`),
        );
      case "MadeToMeasureRollerBlind":
        return variant.images.alternatives[0];
      case "MadeToMeasureRomanBlind":
        return variant.images.alternatives.find((img) => img.endsWith("WSRB"));
      default:
        return "";
    }
  };

  const getPlpUrl = (): string => {
    const category = product.browsePageHierarchy[
      product.browsePageHierarchy.length - 1
    ] as BrowsePageHierarchy;
    return category?.url || "";
  };

  const productTitle = getTitle();
  const plpUrl = getPlpUrl();
  const imageUrl = getImgUrl();
  const selection = getSelection();

  useEffect(() => {
    dispatchAnalyticsEvent(
      "global.impression",
      {
        type: "pdp",
        subType: "m2m summary",
        products: [
          {
            brand: product.brand?.name || "",
            category: getDefaultCategory(product),
            sku: variant.aliases?.skuId,
            price: variant.price?.value,
            productName: variant.title,
            productId: product.aliases?.pimProductId,
          },
        ],
      },
      {},
    );
  }, []);

  return (
    <Modal
      variant="full-screen"
      title={`Your Made to Measure ${productTitle} summary`}
      footer={false}
      isOpen={isOpen}
      onClose={onClose}
      className={styles.ReviewModal}
    >
      <div
        data-testid="m2m-review-container"
        className={styles.ReviewContainer}
      >
        <div data-testid="m2m-review-content" className={styles.ReviewContent}>
          <div
            data-testid="m2m-review-column-left"
            className={styles.ReviewContent__columnLeft}
          >
            <h2>Your selection</h2>
            <ul
              data-testid="m2m-review-config-list"
              className={styles.ReviewContent__list}
            >
              <li data-testid="m2m-review-config-fabric">
                <img
                  className={styles.ReviewContent__image}
                  src={variant.images.primary}
                  data-testid="m2m-review-config-fabric-image"
                />
                <div>
                  <p className={styles.ReviewContent__title}>Fabric</p>
                  <h2 data-testid="m2m-review-config-fabric-description">
                    {formatM2MTitle(
                      variant.title,
                      configurationReqType === "MadeToMeasureRomanBlind",
                    )}{" "}
                    <UnderlinedButton tag="a" type="button" href={plpUrl}>
                      Change fabric
                    </UnderlinedButton>
                  </h2>
                </div>
              </li>
              {selection}
            </ul>
          </div>
          <div
            data-testid="m2m-review-column-right"
            className={styles.ReviewContent__columnRight}
          >
            <div
              data-testid="m2m-review-product-details"
              className={styles.ReviewContent__product}
            >
              <div className={styles.ReviewContent__product__image}>
                <img src={imageUrl} data-testid="m2m-review-product-image" />
              </div>
              <div
                data-testid="m2m-review-product-info"
                className={styles.ReviewContent__product__info}
              >
                <h1
                  className={styles.ReviewContent__product__title}
                  data-testid="m2m-review-product-title"
                >
                  {formatM2MTitle(
                    variant.title,
                    configurationReqType === "MadeToMeasureRomanBlind",
                  )}
                </h1>
                <h2>{`Total ${productTitle} price`}</h2>
                <h2
                  className={styles.ReviewContent__product__price}
                  data-testid="m2m-review-product-price"
                >
                  {totalPrice}
                </h2>
              </div>
            </div>
            <div data-testid="m2m-review-delivery-guarantee">
              <DeliveryAndGuaranteesMessage
                product={product}
                configProps={{
                  messageList: ["includedGuarantees", "deliveryHighlights"],
                }}
                flags={flags}
              />
            </div>
            <div
              className={styles.A2bContainer}
              data-testid="m2m-review-a2b-container"
            >
              <div
                className={styles.A2bContainer__userAcknowledgement}
                data-testid="m2m-review-a2b-user-acknowledgement"
              >
                <UserAcknowledgement product={product} />
              </div>
              <div
                className={styles.A2bContainer__a2bButton}
                data-testid="m2m-review-a2b-cta-section"
              >
                <p>{`Total ${productTitle} price`}</p>
                <p
                  className={styles.A2bContainer__price}
                  data-testid="m2m-review-a2b-cta-price"
                >
                  {totalPrice}
                </p>
                <PrimaryButton
                  tag="button"
                  type="button"
                  disabled={!userAcknowledgementChecked}
                  submitting={isA2bSubmitting}
                  onClick={onClickAddToBasket}
                  data-testid="m2m-review-a2b-cta-button"
                >
                  Add to your basket
                </PrimaryButton>
                {isA2bSuccess && (
                  <ReviewModalBasketSummary
                    quantity={quantityAdded}
                    title={variant.title}
                    configReqType={configurationReqType}
                    configuration={configuration}
                  />
                )}
              </div>
              {isA2bError && <AddToBasketError />}
            </div>
          </div>

          <div
            data-testid="m2m-preview-delivery-details"
            className={styles.ReviewContent__columnFull}
          >
            <ProductAccordion
              product={product}
              runtimeConfig={runtimeConfig}
              experiencesRawObject={[]}
              configProps={{
                accordionConfig: [
                  {
                    name: "deliveryOptions",
                    open: false,
                  },
                ],
              }}
              flags={flags}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
